import { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX, SkipForward, SkipBack, Music, X, Play, Pause } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TRACKS = [
  { name: "Her Space", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/k1jriyuv_HER%20SPACE.mp3" },
  { name: "Cemetery Love (Part One)", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/51cygt8e_CEMETERY%20LOVE%20%28PART%20ONE%29.mp3" },
  { name: "Cemetery Love (Remix)", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/xqu8lj46_CEMETERY%20LOVE%20%28PART%20ONE%29%281%29.mp3" },
  { name: "Feminine Divinity", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/f4c856et_FEMININE%20DIVINITY.mp3" },
  { name: "Singularity (Duet)", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/ygyptzqk_SINGULARITY%20%28DUET%20VERSION%29.mp3" },
  { name: "Perfect Composition", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/dsi3d3ux_Perfect%20Composition.mp3" },
  { name: "Color of Your Lipstick", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/udh6ilij_COLOR%20OF%20YOUR%20LIPSTICK.mp3" },
  { name: "Hot Girls in Abu Dhabi", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/9hh4kze0_HOT%20GIRLS%20IN%20ABU%20DHABI.mp3" },
  { name: "Jamaican Aristocrats", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/walyab5o_JAMICAN%20ARISTOCRATS.mp3" },
  { name: "CarteBlanche JA - Zeitgeist", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/ezft024v_CARTEBLANCHEJA%20%E2%80%93%20ZEITGEIST.mp3" },
  { name: "King Solomon (Extended)", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/kuab0l1r_KING%20SOLOMON%20full%20version%20extended.mp3" },
  { name: "Remember Mi", url: "https://customer-assets.emergentagent.com/job_signature-capture-3/artifacts/pcmke6ay_REMEMBER%20MI.mp3" },
];

export default function MusicPlayer() {
  const [playing, setPlaying] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [showPanel, setShowPanel] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (playing) {
      progressInterval.current = setInterval(() => {
        if (audioRef.current) {
          setProgress(audioRef.current.currentTime);
          setDuration(audioRef.current.duration || 0);
        }
      }, 500);
    } else {
      clearInterval(progressInterval.current);
    }
    return () => clearInterval(progressInterval.current);
  }, [playing]);

  const play = () => {
    if (!audioRef.current) return;
    audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  };

  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setPlaying(false);
  };

  const toggle = () => { playing ? pause() : play(); };

  const switchTrack = (idx) => {
    setTrackIdx(idx);
    setProgress(0);
    setDuration(0);
    if (audioRef.current) {
      audioRef.current.src = TRACKS[idx].url;
      audioRef.current.load();
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const nextTrack = () => switchTrack((trackIdx + 1) % TRACKS.length);
  const prevTrack = () => switchTrack((trackIdx - 1 + TRACKS.length) % TRACKS.length);
  const handleEnded = () => nextTrack();

  const seekTo = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
    setProgress(pct * duration);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={TRACKS[trackIdx].url}
        onEnded={handleEnded}
        preload="auto"
      />

      <button
        data-testid="music-toggle"
        onClick={() => setShowPanel(!showPanel)}
        className={`fixed bottom-6 left-6 z-50 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
          playing
            ? "bg-gold/20 border border-gold/40 text-gold shadow-[0_0_20px_rgba(200,164,92,0.15)]"
            : "bg-white/5 border border-white/10 text-white/40 hover:text-gold hover:border-gold/30"
        }`}
        title="Music Player"
      >
        <Music size={16} className={playing ? "animate-pulse" : ""} />
      </button>

      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-[4.5rem] left-6 z-50 w-72 border border-white/10 rounded-sm overflow-hidden"
            style={{ background: "rgba(8,8,8,0.97)", backdropFilter: "blur(24px)" }}
            data-testid="music-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <p className="text-gold/60 text-[9px] font-body uppercase tracking-[0.2em]">Aristocrats JA</p>
              <button onClick={() => setShowPanel(false)} className="text-white/20 hover:text-white/50 transition-colors"><X size={12} /></button>
            </div>

            {/* Now Playing */}
            <div className="px-4 pb-3">
              <p className="text-white text-sm font-body font-medium truncate" data-testid="now-playing-name">{TRACKS[trackIdx].name}</p>
              <p className="text-white/30 text-[10px] font-body mt-0.5">Track {trackIdx + 1} of {TRACKS.length}</p>
            </div>

            {/* Progress Bar */}
            <div className="px-4 pb-2">
              <div className="w-full h-1.5 bg-white/5 rounded-full cursor-pointer group" onClick={seekTo} data-testid="music-progress-bar">
                <div className="h-full bg-gold/70 rounded-full relative transition-all" style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-white/20 text-[9px] font-body">{fmt(progress)}</span>
                <span className="text-white/20 text-[9px] font-body">{fmt(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4 pb-3">
              <button onClick={prevTrack} className="text-white/30 hover:text-white transition-colors" data-testid="music-prev"><SkipBack size={16} /></button>
              <button onClick={toggle} className="w-10 h-10 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold/25 transition-colors" data-testid="music-play-pause">
                {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
              </button>
              <button onClick={nextTrack} className="text-white/30 hover:text-white transition-colors" data-testid="music-next"><SkipForward size={16} /></button>
            </div>

            {/* Track List */}
            <div className="border-t border-white/5 max-h-52 overflow-y-auto">
              {TRACKS.map((t, i) => (
                <button
                  key={i}
                  onClick={() => switchTrack(i)}
                  data-testid={`music-track-${i}`}
                  className={`w-full text-left px-4 py-2.5 text-xs font-body flex items-center gap-3 transition-colors ${
                    i === trackIdx ? "bg-gold/8 text-gold" : "text-white/40 hover:text-white/70 hover:bg-white/[0.03]"
                  }`}
                >
                  <span className="w-5 text-center text-[10px] shrink-0">{i === trackIdx && playing ? <Volume2 size={12} className="inline animate-pulse" /> : (i + 1)}</span>
                  <span className="truncate">{t.name}</span>
                </button>
              ))}
            </div>

            {/* Volume */}
            <div className="px-4 py-3 border-t border-white/5 flex items-center gap-3">
              <VolumeX size={12} className="text-white/20 shrink-0" />
              <input
                type="range" min="0" max="1" step="0.01" value={volume}
                onChange={e => setVolume(parseFloat(e.target.value))}
                data-testid="music-volume"
                className="w-full h-1 appearance-none bg-white/10 rounded-full accent-gold cursor-pointer"
              />
              <Volume2 size={12} className="text-white/20 shrink-0" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
